export type AnalysisErrorKind = 'unreadable' | 'geometry' | 'timeout' | 'unknown';

export interface AnalysisErrorCopy {
    kind: AnalysisErrorKind;
    title: string;
    detail: string;
    /** Raw analyzer error, for staff only. Empty when the analyzer gave none. */
    technical: string;
}

type RawMessage = { msg?: string | null } | string | null | undefined;

// Patterns seen in production analyzer failures (OpenCascade and Celery).
const KIND_PATTERNS: Array<[AnalysisErrorKind, RegExp]> = [
    ['timeout', /time ?out|timelimit|time limit|exceeded .*s and was marked failed|took too long/i],
    [
        'unreadable',
        /transfer failed|can't read file|cannot read file|StlAPI::Read|not done raised from method Read|unsupported format/i,
    ],
    ['geometry', /out of range|OutOfRange|tesselat|BRep|ShapeFix|invalid shape|null shape/i],
];

export const technicalAnalysisError = (raw: RawMessage): string => {
    const text = typeof raw === 'object' && raw !== null ? raw.msg : raw;
    return (text ?? '').replace(/^Step Analysis Failed with Error:\s*/i, '').trim();
};

export const classifyAnalysisError = (technical: string): AnalysisErrorKind =>
    KIND_PATTERNS.find(([, pattern]) => pattern.test(technical))?.[0] ?? 'unknown';

/**
 * Customer-safe wording for a failed file analysis, with a next step that fits the cause.
 * `canRetry` is true when the viewer has an "Analyze again" button.
 */
export const describeAnalysisError = (
    fileType: '3D' | '2D',
    raw: RawMessage,
    { canRetry = false }: { canRetry?: boolean } = {}
): AnalysisErrorCopy => {
    const technical = technicalAnalysisError(raw);
    const kind = classifyAnalysisError(technical);
    const file = `${fileType} file`;
    const reexport =
        fileType === '3D'
            ? 'export the part again as a STEP file (.step or .stp) and upload it'
            : 'export the drawing again as a PDF and upload it';

    const copy: Record<AnalysisErrorKind, Pick<AnalysisErrorCopy, 'title' | 'detail'>> = {
        unreadable: {
            title: `We couldn't open this ${file}`,
            detail: `The file may be damaged or saved in a format version we can't read. Please ${reexport}.`,
        },
        geometry: {
            title: `We couldn't process the geometry in this ${file}`,
            detail:
                'The model may have missing or overlapping surfaces. Repair it in your CAD software or export it as a single solid, then upload it again.',
        },
        timeout: {
            title: `Analysis of this ${file} took too long`,
            detail: canRetry
                ? 'Large or highly detailed models can time out. Try the analysis again, or upload a simplified version of the part.'
                : 'Large or highly detailed models can time out. Please upload a simplified version of the part, or contact us and we will review it.',
        },
        unknown: {
            title: `We couldn't analyze this ${file}`,
            detail: canRetry
                ? `Try the analysis again. If it keeps failing, ${reexport}.`
                : `Something went wrong while analyzing it. Please ${reexport}, or contact us and we will review it.`,
        },
    };

    return { kind, technical, ...copy[kind] };
};
